/**
 * HealthMartModal — P.AI Health Mart "Coming Soon" preview.
 *
 * Extracted from HomePage (June 12, 2026) so it can be opened from the
 * consolidated investor gate on /investor-access. Content unchanged.
 */
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { ScrollArea } from '@/components/ui/scroll-area';
import { ShoppingBag } from 'lucide-react';

export default function HealthMartModal({ open, onOpenChange }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh]" data-testid="health-mart-modal">
        <ScrollArea className="max-h-[75vh]">
          <div className="p-4">
            <div className="text-center mb-6">
              <ShoppingBag className="w-12 h-12 text-emerald-600 mx-auto mb-3" />
              <h3 className="text-2xl font-bold text-black mb-1">
                <span className="text-[#EF4444] font-extrabold">P.AI</span> <span className="text-[#EF4444] font-extrabold">Health Mart</span>
              </h3>
              <p className="text-lg font-semibold text-emerald-700 mb-1">Coming Soon</p>
              <p className="text-sm text-slate-500">Exclusively integrated with P.AI Global Health (Patient A.i)</p>
            </div>

            {/* Trust Banner */}
            <div className="bg-emerald-50 border-2 border-emerald-300 rounded-xl p-4 mb-6 text-center">
              <p className="text-base font-bold text-emerald-800 mb-1">No Advertisements. Transparency First. Trust-Oriented Discovery.</p>
              <p className="text-sm text-emerald-700">No paid rankings. No deceptive practices. Every transaction is traceable.</p>
              <p className="text-xs text-emerald-600 mt-1 font-medium">Blockchain-Enabled Audit Trail — Full Traceability</p>
            </div>

            {/* High-Resource Categories */}
            <h4 className="font-bold text-base text-black mb-4">Curated High-Resource Categories</h4>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
              <div className="border-2 border-slate-200 rounded-xl p-4 hover:border-[#2563EB] transition-colors">
                <div className="w-8 h-8 rounded-lg bg-blue-100 text-[#2563EB] flex items-center justify-center mb-2 text-lg font-bold">1</div>
                <h5 className="font-bold text-sm text-black mb-1">GLP-1 Agonists & Metabolic Health</h5>
                <p className="text-xs text-slate-600">Direct-to-patient access pathways for weight management and diabetic care. Semaglutide, Tirzepatide, Liraglutide — designed to reduce intermediary cost barriers.</p>
                <p className="text-[10px] text-[#2563EB] font-medium mt-2">Topol-Swisher evidence-informed</p>
              </div>

              <div className="border-2 border-slate-200 rounded-xl p-4 hover:border-[#2563EB] transition-colors">
                <div className="w-8 h-8 rounded-lg bg-blue-100 text-[#2563EB] flex items-center justify-center mb-2 text-lg font-bold">2</div>
                <h5 className="font-bold text-sm text-black mb-1">Specialty Biologics & Infusions</h5>
                <p className="text-xs text-slate-600">Coordination for complex medication management — Rheumatology (Humira, Remicade), Gastroenterology, Neurology biologics. Curated providers with review status shown.</p>
                <p className="text-[10px] text-[#2563EB] font-medium mt-2">Clinically informed referral pathway</p>
              </div>

              <div className="border-2 border-slate-200 rounded-xl p-4 hover:border-[#2563EB] transition-colors">
                <div className="w-8 h-8 rounded-lg bg-blue-100 text-[#2563EB] flex items-center justify-center mb-2 text-lg font-bold">3</div>
                <h5 className="font-bold text-sm text-black mb-1">Oncology Supportive Care</h5>
                <p className="text-xs text-slate-600">Curated marketplace for palliative care, nutritional support, and side-effect management resources for cancer patients. Clinically informed curation — no paid rankings.</p>
                <p className="text-[10px] text-[#2563EB] font-medium mt-2">Patient-safety first curation</p>
              </div>

              <div className="border-2 border-slate-200 rounded-xl p-4 hover:border-[#2563EB] transition-colors">
                <div className="w-8 h-8 rounded-lg bg-blue-100 text-[#2563EB] flex items-center justify-center mb-2 text-lg font-bold">4</div>
                <h5 className="font-bold text-sm text-black mb-1">DME, Home Healthcare & Second Opinion</h5>
                <p className="text-xs text-slate-600">Automated referral loops for verified Home Healthcare services, Second Opinion specialists, and specialty DME — Oxygen, CPAP, mobility aids, monitoring devices.</p>
                <p className="text-[10px] text-[#2563EB] font-medium mt-2">Triage-to-utility referral loop</p>
              </div>
            </div>

            {/* Category 5: Curated Hospice Centers */}
            <div className="border-2 border-slate-200 rounded-xl p-4 hover:border-[#2563EB] transition-colors sm:col-span-2">
              <div className="w-8 h-8 rounded-lg bg-blue-100 text-[#2563EB] flex items-center justify-center mb-2 text-lg font-bold">5</div>
              <h5 className="font-bold text-sm text-black mb-1">Curated Hospice Centers</h5>
              <p className="text-xs text-slate-600">A trust-oriented navigation resource for patients and families seeking hospice support. Curated listings with service details, languages, and care type. No unverified claims — transparency and dignity first.</p>
              <p className="text-[10px] text-slate-500 mt-2">Labels: Curated Listing | Review Pending | Family Resource | Multilingual Support</p>
              <p className="text-[10px] text-[#2563EB] font-medium mt-1">Listings include: hospice name, city/state, service region, phone, care type, languages, in-home/facility, Medicare/Medicaid status</p>
            </div>

            {/* How It Works */}
            <div className="bg-slate-50 rounded-xl p-4 mb-6 border border-slate-200 mt-6">
              <h4 className="font-bold text-sm text-black mb-2">How It Works</h4>
              <div className="space-y-2 text-xs text-slate-700">
                <div className="flex items-start gap-2">
                  <span className="font-bold text-[#2563EB] mt-0.5">1.</span>
                  <span>Clinical encounter generates validated health profile (SOAP note + risk flags)</span>
                </div>
                <div className="flex items-start gap-2">
                  <span className="font-bold text-[#2563EB] mt-0.5">2.</span>
                  <span>System identifies clinically relevant resource needs — relevance-first guidance, not advertisements</span>
                </div>
                <div className="flex items-start gap-2">
                  <span className="font-bold text-[#2563EB] mt-0.5">3.</span>
                  <span>Patient-initiated marketplace — you browse curated providers, you decide</span>
                </div>
                <div className="flex items-start gap-2">
                  <span className="font-bold text-[#2563EB] mt-0.5">4.</span>
                  <span>Blockchain-enabled architecture designed for full traceability of listings and referrals</span>
                </div>
              </div>
            </div>

            {/* Unit Economics */}
            <div className="bg-[#2563EB]/5 rounded-xl p-4 mb-6 border border-[#2563EB]/20 text-center">
              <p className="text-xs text-slate-500 mb-1">Target unit economics</p>
              <p className="text-2xl font-bold text-[#2563EB]">$0.23 <span className="text-sm font-normal text-slate-500">per validated encounter</span></p>
              <p className="text-xs text-slate-500 mt-1">Full clinical workup: SOAP + CPT + Resource Matching</p>
            </div>

            {/* Revenue Model */}
            <div className="mb-6">
              <h4 className="font-bold text-sm text-black mb-2">Revenue Architecture</h4>
              <div className="text-xs text-slate-600 space-y-1">
                <p><strong>Patient:</strong> Free forever — health equity by design</p>
                <p><strong>Provider:</strong> Free SOAP notes + clinical intelligence</p>
                <p><strong>Revenue:</strong> Transparent referral fees from verified marketplace listings (Pharma, Payers, DME, Home Health)</p>
              </div>
            </div>

            {/* Legal Separation */}
            <div className="border-t border-slate-200 pt-4 text-center">
              <p className="text-[10px] text-slate-400 mb-1">
                <span className="text-[#EF4444] font-bold">P.AI</span> Health Mart is a separate DTC Utility Platform, legally distinct from PGH Sovereign OS (Clinical Intelligence Engine).
              </p>
              <p className="text-[10px] text-slate-400">
                Exclusively and tightly integrated with P.AI Global Health (Patient A.i) — not available as a standalone marketplace.
              </p>
            </div>
          </div>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
}
